import { MEMBERSHIP_TYPE, MEMBERSHIP_PRICE_TYPE } from '../enums';
import { CategorizedMemberships, MembershipModel, MembershipPriceModel, MembershipTier, OneRoundBoost, UserMembershipModel } from '../models/membership.model';
import { TIER_SLUG_MAP, TIER_EXTRAS } from '../constants/membership.constants';

export const getMembershipStyle = (membershipType: MEMBERSHIP_TYPE): { icon: string; color: string } => {
   switch (membershipType) {
      case MEMBERSHIP_TYPE.GOLD:
         return { icon: '/assets/icons/memberships/gold-icon.svg', color: '#BFC951' };
      case MEMBERSHIP_TYPE.WHITE:
         return { icon: '/assets/icons/memberships/white-icon.svg', color: '#F5F5F6' };
      case MEMBERSHIP_TYPE.DIAMOND:
         return { icon: '/assets/icons/memberships/diamond-icon.svg', color: '#2498A0' };
      case MEMBERSHIP_TYPE.SILVER:
         return { icon: '/assets/icons/memberships/silver-icon.svg', color: '#868DC0' };
      case MEMBERSHIP_TYPE.GREEN:
      default:
         return { icon: '/assets/icons/memberships/green-icon.svg', color: '#68C072' };
   }
};

export function getPrice(prices: MembershipPriceModel[] | null | undefined, type: MEMBERSHIP_PRICE_TYPE): number {
   return prices?.find((p) => p.type === type)?.price ?? 0;
}

export function mapMembershipToTier(m: MembershipModel): MembershipTier | null {
   const slug = TIER_SLUG_MAP[m.type];
   if (!slug) return null;

   const extras = TIER_EXTRAS[m.type];
   if (!extras) return null;

   const monthlyPrice = getPrice(m.prices, MEMBERSHIP_PRICE_TYPE.MONTHLY);
   const annualTotal = getPrice(m.prices, MEMBERSHIP_PRICE_TYPE.ANNUALLY);
   const annualPerMonth = annualTotal > 0 ? annualTotal / 12 : monthlyPrice;

   return {
      id: m.id,
      type: m.type,
      name: `${m.type.charAt(0)}${m.type.slice(1).toLowerCase()} Membership`,
      slug,
      multiplier: {
         monthly: m.monthlyPointsMultiplier,
         annual: m.annualPointsMultiplier,
      },
      multiplierDesc: `One round does the work of ${m.annualPointsMultiplier}.`,
      price: { monthly: monthlyPrice, annualPerMonth, annualTotal },
      cta: m.type === 'DIAMOND' ? 'Apply to Become a Diamond Member' : `Become a ${m.type.charAt(0)}${m.type.slice(1).toLowerCase()} Member`,
      icons: {
         dge: `/assets/icons/memberships/dge-${slug}-icon.png`,
         entry: `/assets/icons/memberships/entry-${slug}-icon.png`,
         spin: `/assets/icons/memberships/spin-${slug}-icon.png`,
      },
      ...extras,
   };
}

/**
 * Separates active memberships into:
 * - primary: the paid subscription, or the highest-sort non-one-round membership
 * - oneRoundBoosts: one-round memberships grouped by tier, only those higher than primary
 */
export function categorizeMemberships(memberships: UserMembershipModel[]): CategorizedMemberships {
   const subscriptions = memberships.filter((m) => m.stripeSubscriptionId);
   const nonOneRound = memberships.filter(
      (m) => m.type !== MEMBERSHIP_PRICE_TYPE.ONE_ROUND && m.type !== MEMBERSHIP_PRICE_TYPE.ONE_DAY
   );
   const oneRounds = memberships.filter(
      (m) => m.type === MEMBERSHIP_PRICE_TYPE.ONE_ROUND || m.type === MEMBERSHIP_PRICE_TYPE.ONE_DAY
   );

   // Primary = paid subscription first, else highest-sort non-one-round, else highest one-round
   let primary: UserMembershipModel | null = null;
   if (subscriptions.length > 0) {
      primary = subscriptions.reduce((best, m) =>
         (m.membershipSort ?? 0) > (best.membershipSort ?? 0) ? m : best
      );
   } else if (nonOneRound.length > 0) {
      primary = nonOneRound.reduce((best, m) =>
         (m.membershipSort ?? 0) > (best.membershipSort ?? 0) ? m : best
      );
   } else if (oneRounds.length > 0) {
      primary = oneRounds.reduce((best, m) =>
         (m.membershipSort ?? 0) > (best.membershipSort ?? 0) ? m : best
      );
   }

   const primarySort = primary?.membershipSort ?? 0;

   // Group one-round memberships by tier, only those higher than primary
   const boostMap = new Map<MEMBERSHIP_TYPE, OneRoundBoost>();
   for (const m of oneRounds) {
      if (m.id === primary?.id) continue;
      const sort = m.membershipSort ?? 0;
      if (sort <= primarySort) continue;
      const type = m.membershipType ?? MEMBERSHIP_TYPE.GREEN;
      const existing = boostMap.get(type);
      if (existing) {
         existing.count++;
      } else {
         boostMap.set(type, { membershipType: type, membershipSort: sort, count: 1 });
      }
   }

   const oneRoundBoosts = Array.from(boostMap.values()).sort((a, b) => b.membershipSort - a.membershipSort);

   return { primary, oneRoundBoosts };
}

export function formatPriceType(type: MEMBERSHIP_PRICE_TYPE): string {
   switch (type) {
      case MEMBERSHIP_PRICE_TYPE.ONE_ROUND: return 'One Round';
      case MEMBERSHIP_PRICE_TYPE.ONE_DAY: return 'One Day';
      case MEMBERSHIP_PRICE_TYPE.MONTHLY: return 'Monthly';
      case MEMBERSHIP_PRICE_TYPE.ANNUALLY: return 'Annual';
      case MEMBERSHIP_PRICE_TYPE.THREE_MONTH: return '3-Month';
      case MEMBERSHIP_PRICE_TYPE.SIX_MONTH: return '6-Month';
      default: return type;
   }
}
